import React, { useEffect, useState } from 'react';

export function useSkeletonFailsafe(isLoading, maxMs = 2500) {
  const [timedOut, setTimedOut] = useState(false);

  useEffect(() => {
    if (!isLoading) {
      setTimedOut(false);
      return undefined;
    }
    const timeout = setTimeout(() => setTimedOut(true), maxMs);
    return () => clearTimeout(timeout);
  }, [isLoading, maxMs]);

  return timedOut;
}

export const SkeletonBlock = ({ className = '', style }) => (
  <div className={`fg-skeleton ${className}`.trim()} style={style} aria-hidden="true" />
);

export const SkeletonText = ({ width = '100%', className = '' }) => (
  <SkeletonBlock className={`fg-skeleton--text ${className}`.trim()} style={{ width }} />
);

export const SkeletonTitle = ({ width = '42%' }) => (
  <SkeletonBlock className="fg-skeleton--title" style={{ width }} />
);

export const SkeletonCircle = ({ size = 42 }) => (
  <SkeletonBlock className="fg-skeleton--circle" style={{ width: size, height: size }} />
);

export const SkeletonButton = ({ width = 120 }) => (
  <SkeletonBlock className="fg-skeleton--button" style={{ width, height: 40 }} />
);

export const SkeletonPill = ({ width = 88 }) => (
  <SkeletonBlock className="fg-skeleton--pill" style={{ width, height: 28 }} />
);

export const SkeletonCard = ({ children, className = '' }) => (
  <div className={`fg-skeleton-card ${className}`.trim()} aria-hidden="true">
    {children}
  </div>
);

const srOnly = (
  <span className="sr-only">Loading FundGenius workspace...</span>
);

export const FundingRosterTableSkeleton = ({ rows = 7 }) => (
  <div className="fg-skeleton-table" aria-busy="true">
    {srOnly}
    <div className="fg-skeleton-row fg-skeleton-row--header">
      {Array.from({ length: 8 }).map((_, index) => (
        <SkeletonText key={`head-${index}`} width={`${60 + (index % 3) * 12}px`} />
      ))}
    </div>
    {Array.from({ length: rows }).map((_, rowIndex) => (
      <div key={`row-${rowIndex}`} className="fg-skeleton-row">
        <SkeletonCircle size={18} />
        <SkeletonText width="18%" />
        <SkeletonText width="14%" />
        <SkeletonText width="16%" />
        <SkeletonText width="12%" />
        <SkeletonPill width={92} />
        <SkeletonText width="10%" />
        <SkeletonButton width={96} />
      </div>
    ))}
  </div>
);

export const FundingClientsPageSkeleton = () => (
  <div className="fg-skeleton-page" aria-busy="true">
    {srOnly}
    <div className="fg-skeleton-page__header">
      <SkeletonText width="120px" />
      <SkeletonTitle width="280px" />
      <SkeletonText width="420px" />
      <SkeletonButton width={160} />
    </div>
    <div className="fg-skeleton-grid fg-skeleton-grid--pipeline">
      {Array.from({ length: 7 }).map((_, index) => (
        <SkeletonCard key={`pipe-${index}`}>
          <SkeletonText width="70%" />
          <SkeletonText width="50%" />
        </SkeletonCard>
      ))}
    </div>
    <SkeletonCard className="fg-skeleton-panel">
      <SkeletonTitle width="180px" />
      <SkeletonText width="320px" />
      <div className="fg-skeleton-toolbar">
        <SkeletonText width="220px" />
        <SkeletonText width="140px" />
        <SkeletonText width="140px" />
      </div>
      <FundingRosterTableSkeleton />
    </SkeletonCard>
  </div>
);

export const FundingApplicationDashboardSkeleton = () => (
  <div className="fg-skeleton-dashboard" aria-busy="true">
    {srOnly}
    <div className="fg-skeleton-grid fg-skeleton-grid--kpis">
      {Array.from({ length: 6 }).map((_, index) => (
        <SkeletonCard key={`kpi-${index}`}>
          <SkeletonText width="60%" />
          <SkeletonTitle width="40%" />
        </SkeletonCard>
      ))}
    </div>
    <div className="fg-skeleton-grid fg-skeleton-grid--amounts">
      {Array.from({ length: 3 }).map((_, index) => (
        <SkeletonCard key={`amt-${index}`}>
          <SkeletonText width="55%" />
          <SkeletonTitle width="35%" />
        </SkeletonCard>
      ))}
    </div>
    <div className="fg-skeleton-grid fg-skeleton-grid--charts">
      <SkeletonCard><SkeletonCircle size={120} /><SkeletonText width="70%" /></SkeletonCard>
      <SkeletonCard>
        {Array.from({ length: 6 }).map((_, index) => (
          <SkeletonText key={`bar-${index}`} width={`${55 + index * 4}%`} />
        ))}
      </SkeletonCard>
      <SkeletonCard><SkeletonCircle size={120} /><SkeletonText width="60%" /></SkeletonCard>
    </div>
    <SkeletonCard>
      <SkeletonTitle width="220px" />
      <FundingRosterTableSkeleton rows={5} />
    </SkeletonCard>
  </div>
);

export const FundingClientDetailsSkeleton = () => (
  <div className="fg-skeleton-details" aria-busy="true">
    {srOnly}
    <SkeletonCard className="fg-skeleton-nav">
      <div className="fg-skeleton-toolbar">
        <SkeletonCircle size={38} />
        <SkeletonTitle width="180px" />
        <SkeletonPill width={52} />
        {Array.from({ length: 5 }).map((_, index) => (
          <SkeletonCircle key={`nav-${index}`} size={42} />
        ))}
      </div>
    </SkeletonCard>
    <SkeletonCard className="fg-skeleton-client-header">
      <div className="fg-skeleton-toolbar">
        <SkeletonCircle size={64} />
        <div className="fg-skeleton-stack">
          <SkeletonTitle width="220px" />
          <SkeletonText width="180px" />
          <div className="fg-skeleton-inline">
            <SkeletonPill />
            <SkeletonPill />
            <SkeletonPill />
          </div>
        </div>
        <SkeletonButton width={140} />
        <SkeletonButton width={160} />
      </div>
      <div className="fg-skeleton-toolbar">
        <SkeletonText width="180px" />
        <SkeletonButton width={120} />
        <SkeletonPill />
        <SkeletonPill />
        <SkeletonPill />
      </div>
    </SkeletonCard>
    <div className="fg-skeleton-inline">
      <SkeletonPill width={120} />
      <SkeletonPill width={120} />
      <SkeletonPill width={120} />
    </div>
    <FundingApplicationDashboardSkeleton />
  </div>
);

export const FundingDrawerSkeleton = () => (
  <div className="fg-skeleton-drawer" aria-busy="true">
    {srOnly}
    <SkeletonTitle width="220px" />
    <SkeletonText width="80%" />
    {Array.from({ length: 3 }).map((_, index) => (
      <SkeletonCard key={`section-${index}`}>
        <SkeletonTitle width="160px" />
        <div className="fg-skeleton-grid fg-skeleton-grid--fields">
          <SkeletonText />
          <SkeletonText />
          <SkeletonText />
          <SkeletonText />
        </div>
      </SkeletonCard>
    ))}
    <div className="fg-skeleton-toolbar">
      <SkeletonButton />
      <SkeletonButton />
      <SkeletonButton width={160} />
    </div>
  </div>
);
